import { Loading } from '@veupathdb/wdk-client/lib/Components';
import { useSpecificEntityCounts } from '../../core/hooks/entityCounts';
import { useDataClient, useStudyMetadata } from '../../core/hooks/workspace';
import { isEqual } from 'lodash';
import { RunComputeButton } from '../../core/components/computations/RunComputeButton';
import { useComputeJobStatus } from '../../core/components/computations/ComputeJobStatusHook';
import { NotebookCell, NotebookCellProps } from '../NotebookCell';
import { plugins } from '../../core/components/computations/plugins';
import type { CountGatingResult } from '../../core/components/computations/Types';
import { ComputeCellDescriptor } from '../Types';
import { useCachedPromise } from '../../core/hooks/cachedPromise';
import ExpandablePanel from '@veupathdb/coreui/lib/components/containers/ExpandablePanel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog from '@veupathdb/wdk-client/lib/Components/Overlays/Dialog';
import { Link } from 'react-router-dom';
import { NotebookCellPreHeader } from '../NotebookCellPreHeader';
import { red } from '@veupathdb/coreui/lib/definitions/colors';
import { useDeepValue } from '../../core/hooks/immutability';
import { useDebounce } from '../../core/hooks/debouncing';

export function ComputeNotebookCell(
  props: NotebookCellProps<ComputeCellDescriptor>
) {
  const {
    analysisState,
    cell,
    isDisabled,
    wdkState,
    projectId,
    stepNumber,
    stepNumbers,
  } = props;
  const { analysis } = analysisState;
  if (analysis == null) throw new Error('Cannot find analysis.');

  const {
    computationName,
    computationId,
    cells,
    getAdditionalCollectionPredicate,
    hidden = false,
    sharedInputNames,
    sharedInputsCellId,
    readonlyInputNames,
    initialPanelState = 'open',
  } = cell;
  const computation = analysis.descriptor.computations.find(
    (comp) => comp.computationId === computationId
  );
  if (computation == null) throw new Error('Cannot find computation.');

  const hasUnsetSharedInputs =
    sharedInputNames != null &&
    sharedInputNames.some(
      (name) =>
        !(computation.descriptor.configuration as Record<string, unknown>)?.[
          name
        ]
    );

  const sharedInputsStepNumber = sharedInputsCellId
    ? stepNumbers?.get(sharedInputsCellId)
    : undefined;

  const systemState: 'open' | 'closed' = hasUnsetSharedInputs
    ? 'closed'
    : initialPanelState;

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [panelState, setPanelState] = useState<'open' | 'closed'>(systemState);
  const [userPanelState, setUserPanelState] = useState(panelState);

  useEffect(() => {
    const s: 'open' | 'closed' = hasUnsetSharedInputs
      ? 'closed'
      : initialPanelState;
    setPanelState(s);
  }, [hasUnsetSharedInputs, initialPanelState]);

  // fetch 'apps'
  const dataClient = useDataClient();
  const fetchApps = async () => {
    let { apps } = await dataClient.getApps();
    return { apps };
  };
  const apps = useCachedPromise(fetchApps, ['fetchApps']);
  const appOverview =
    apps && apps.value?.apps.find((app) => app.name === computationName);

  // Debounce filters so child cells and entity counts don't react to every
  // rapid filter change. useComputeJobStatus receives the original (un-debounced)
  // analysis and handles its own debouncing internally.
  const debouncedFilters = useDebounce(
    useDeepValue(analysis.descriptor.subset.descriptor),
    2000
  );

  const { rootEntity } = useStudyMetadata();

  // Notebook computation plugins only ever gate/configure on the root
  // (sample) entity's count, so fetch just the root entity here instead of
  // fanning out over every entity in the study via useEntityCounts.
  const totalCountsResult = useSpecificEntityCounts(undefined, [rootEntity]);
  const filteredCountsResult = useSpecificEntityCounts(debouncedFilters, [
    rootEntity,
  ]);
  const plugin = plugins[computation.descriptor.type];
  if (plugin == null) throw new Error('Computation plugin not found.');

  // Update a computation's configuration property. All logic runs inside the
  // functional update so it always operates on the latest state, avoiding stale
  // closure issues during the WDK param round-trip.
  const { setComputations } = analysisState;
  const changeConfigHandler = useCallback(
    (propertyName: string, value?: any) => {
      setComputations((computations) => {
        const comp = computations.find(
          (c) => c.computationId === computationId
        );
        if (!comp) return computations;

        const currentConfig =
          comp.descriptor.configuration &&
          typeof comp.descriptor.configuration === 'object'
            ? comp.descriptor.configuration
            : {};

        const resolvedValue =
          typeof value === 'function'
            ? value((currentConfig as Record<string, any>)[propertyName])
            : value;
        const updatedConfig = {
          ...currentConfig,
          [propertyName]: resolvedValue,
        };

        if (isEqual(currentConfig, updatedConfig)) return computations;

        return computations.map((c) =>
          c.computationId === computationId
            ? {
                ...c,
                descriptor: { ...c.descriptor, configuration: updatedConfig },
              }
            : c
        );
      });
    },
    [setComputations, computationId]
  );

  const { jobStatus, createJob } = useComputeJobStatus(
    analysis,
    computation,
    appOverview?.computeName ?? ''
  );

  const isComputationConfigurationValid = !!plugin?.isConfigurationComplete(
    computation.descriptor.configuration
  );

  const pluginCountGating = plugin.getCountWarning?.(
    {
      root: {
        pending: filteredCountsResult.pending,
        value: filteredCountsResult.value?.[rootEntity.id],
      },
    },
    computation.descriptor.configuration
  );

  // Per-group count gating reported by the configuration component
  const [configCountGating, setConfigCountGating] = useState<
    CountGatingResult | undefined
  >();

  // Merge: take the more severe of pluginCountGating and configCountGating.
  // Severity: warning > pending > ok > undefined
  const countGating = useMemo(() => {
    const results = [pluginCountGating, configCountGating].filter(
      (r): r is CountGatingResult => r != null
    );
    if (results.some((r) => r.type === 'warning'))
      return results.find((r) => r.type === 'warning');
    if (results.some((r) => r.type === 'pending'))
      return results.find((r) => r.type === 'pending');
    if (results.some((r) => r.type === 'ok'))
      return results.find((r) => r.type === 'ok');
    return pluginCountGating;
  }, [pluginCountGating, configCountGating]);

  // Prep any additional restrictions on collections, if defined
  const additionalCollectionPredicate =
    getAdditionalCollectionPredicate &&
    getAdditionalCollectionPredicate(projectId);

  // Run the compute if we're all set to go. Useful when there is a default configuration.
  useEffect(() => {
    if (
      isComputationConfigurationValid &&
      jobStatus === 'no-such-job' &&
      hidden
    ) {
      createJob();
    }
  }, [isComputationConfigurationValid, jobStatus, createJob, hidden]);

  // Computes manage the collapse/expand state of child visualizations
  // visualizations are always by default 'closed' until their computations are ready
  const systemChildState =
    isComputationConfigurationValid && jobStatus === 'complete'
      ? 'open'
      : 'closed';

  const [childrenPanelState, setChildrenPanelState] = useState<
    'open' | 'closed'
  >(systemChildState);

  // Expand child visualization(s) if the compute is complete
  useEffect(() => {
    if (isComputationConfigurationValid && jobStatus === 'complete') {
      setChildrenPanelState('open');
    }
  }, [isComputationConfigurationValid, jobStatus]);

  // Show error dialog when hidden compute fails
  useEffect(() => {
    if (hidden && jobStatus === 'failed') {
      setShowErrorDialog(true);
    }
  }, [hidden, jobStatus]);

  // Wrap analysisState with debounced filters for child cells.
  // useComputeJobStatus above receives the original analysis so it can apply
  // its own internal debounce independently.
  const debouncedAnalysisState = useMemo(
    () => ({
      ...analysisState,
      analysis: {
        ...analysis,
        descriptor: {
          ...analysis.descriptor,
          subset: {
            ...analysis.descriptor.subset,
            descriptor: debouncedFilters ?? [],
          },
        },
      },
    }),
    [analysisState, analysis, debouncedFilters]
  );

  return computation && appOverview ? (
    <>
      {/* Error Dialog */}
      <Dialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        aria-labelledby="compute-error-dialog-title"
        title="Computation failed"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: '600px',
            padding: '1em',
            alignItems: 'center',
            fontSize: '1.2em',
            height: '130px',
            textAlign: 'center',
            margin: '0.5em 0',
          }}
        >
          <p>
            The background {cell.title + ' ' || ''}computation for has failed.{' '}
            <strong>
              Please <Link to="/contact-us">contact us</Link> for assistance.
            </strong>
          </p>
          <p>After closing this dialog, you may continue with your search.</p>
        </div>
      </Dialog>
      {(() => {
        const sharedPluginProps = {
          analysisState,
          computationId,
          totalCounts: totalCountsResult,
          filteredCounts: filteredCountsResult,
          visualizationId: 'not_used', // irrelevant because we have our own changeConfigHandler
          addNewComputation: () => {}, // also irrelevant for us because we add the computation elsewhere
          computationAppOverview: appOverview,
          geoConfigs: [],
          changeConfigHandlerOverride: changeConfigHandler,
          showStepNumber: false,
          showExpandableHelp: false, // no expandable sections within an expandable element.
          additionalCollectionPredicate,
          readonlyInputNames: [
            ...(sharedInputNames ?? []),
            ...(readonlyInputNames ?? []),
          ],
          onCountGatingChange: setConfigCountGating,
          autoSelectFeatured: true,
        };

        if (hidden) {
          return (
            <plugin.configurationComponent
              {...sharedPluginProps}
              hideConfigurationComponent={true}
            />
          );
        }

        return (
          <>
            <NotebookCellPreHeader cell={cell} stepNumber={stepNumber} />
            {hasUnsetSharedInputs && sharedInputsStepNumber != null && (
              <p
                className="SharedInputsHint"
                style={
                  userPanelState === 'open' ? { color: red[600] } : undefined
                }
              >
                Complete step {sharedInputsStepNumber} to enable this analysis.
              </p>
            )}
            <ExpandablePanel
              title={cell.title}
              subTitle={''}
              state={panelState}
              onStateChange={setUserPanelState}
              themeRole="primary"
            >
              <div
                className={
                  'NotebookCellContent' + (isDisabled ? ' disabled' : '')
                }
              >
                <plugin.configurationComponent
                  {...sharedPluginProps}
                  hideConfigurationComponent={false}
                />
                <RunComputeButton
                  computationAppOverview={appOverview}
                  status={jobStatus}
                  isConfigured={isComputationConfigurationValid}
                  createJob={createJob}
                  countGating={countGating}
                />
              </div>
            </ExpandablePanel>
          </>
        );
      })()}
      {cells &&
        cells.map((subCell, index) => {
          const isSubCellDisabled =
            jobStatus !== 'complete' && subCell.type !== 'text';

          return (
            <NotebookCell
              key={index}
              analysisState={debouncedAnalysisState}
              cell={subCell}
              isDisabled={isSubCellDisabled}
              wdkState={wdkState}
              computeJobStatus={jobStatus}
              stepNumber={stepNumbers?.get(subCell.id)}
              stepNumbers={stepNumbers}
              expandedPanelState={childrenPanelState}
            />
          );
        })}
    </>
  ) : (
    <Loading />
  );
}
